import { useAuth } from "../context/AuthContext";

/**
 * What the signed-in customer's account lifecycle state allows.
 *
 * The customer-side sibling of `usePermissions()`. Mirrors the backend registry
 * in `app/core/account_capabilities.py` - see `backend/docs/account_capabilities.md`
 * for the matrix. This is presentation only: every restricted action is
 * independently refused by the API, so hiding or disabling a control is a
 * usability choice, never the security boundary.
 *
 * `/me` has always returned `account_status`, `account_status_reason` and
 * `account_status_changed_at`; until now the customer app read none of them, so
 * a suspended customer saw a normal dashboard and only discovered the problem
 * when an action failed.
 */

export const ACCOUNT_STATUS = {
  PENDING_VERIFICATION: "pending_verification",
  ACTIVE: "active",
  SUSPENDED: "suspended",
  CLOSED: "closed",
};

/**
 * Customer-facing copy per state.
 *
 * Deliberately does not surface `account_status_reason`. That field holds staff
 * shorthand written for an internal audience ("suspected structuring"), and
 * showing it would leak compliance reasoning to the person it concerns. The
 * customer is told their state and where to go.
 */
const STATUS_COPY = {
  [ACCOUNT_STATUS.PENDING_VERIFICATION]: {
    tone: "info",
    title: "Finish setting up your account",
    body: "Verify your email address to start sending money.",
  },
  [ACCOUNT_STATUS.SUSPENDED]: {
    tone: "warning",
    title: "Your account is suspended",
    body:
      "You can still view your history and track transfers already in progress, " +
      "but you cannot send money right now. Please contact support.",
  },
  [ACCOUNT_STATUS.CLOSED]: {
    tone: "closed",
    title: "Your account is closed",
    body:
      "Your transaction history stays available to you. You cannot send money " +
      "or add recipients. Please contact support if you need help.",
  },
};

export function useAccountStatus() {
  const { user, loading } = useAuth();
  const status = user?.account_status ?? null;

  // Fail closed while the profile is still loading or absent: better to show a
  // disabled control briefly than to offer an action the API will refuse.
  const canTransact = status === ACCOUNT_STATUS.ACTIVE;
  const notice = status ? STATUS_COPY[status] ?? null : null;

  return {
    status,
    /** True only for a fully active account. */
    canTransact,
    /** True when there is something worth telling the customer about. */
    isRestricted: Boolean(notice),
    /** `{ tone, title, body }` for the banner, or null when nothing to say. */
    notice,
    /** When staff last changed the state, for "since <date>" copy. */
    changedAt: user?.account_status_changed_at ?? null,
    isLoadingAccountStatus: loading,
  };
}
